<template>
  <div class="expiration-date-editor">
    <div class="triangle" :class="trianglePositionClass" :style="triangleStyle"></div>
    <div class="expiration-date-editor-header">
      <BadgeExpirationWarning :expiration-date="localExpirationType === 'on_this_date' ? localExpirationDate : null"
        :expiration-days="localExpirationType === 'obsolete_days' ? localDays : null"
        :state="(isNew || hasChanges) && !(isExpirationProvided && !hasChanges)" />
      <v-btn icon small @click="emit('close')" class="close-exp">
        <v-icon size="18">mdi-close</v-icon>
      </v-btn>
    </div>
    <div class="expiration-date-editor-content">
      <div v-if="badgeStatus !== 'Given' && badgeStatus !== 'Obsolete'" class="expiration-radios">
        <div class="radio-wrapper">
          <input type="radio" id="fixed_date" value="on_this_date" v-model="localExpirationType" class="filter-radio" />
          <label for="fixed_date" class="radio-label">Set to expire on</label>
        </div>
      </div>
      <div v-if="localExpirationType === 'on_this_date'" class="expiration-date-picker"
        :class="{ 'date-picker-changed': hasChanges }">
        <DatePicker :value="localExpirationDate" @onApplyDate="handleApplyDate" :modal="true" :minDate="minDate" />
      </div>
      <div v-if="badgeStatus !== 'Given' && badgeStatus !== 'Obsolete'" class="expiration-radios">
        <div class="radio-wrapper">
          <input type="radio" id="obsolete_days" value="obsolete_days" v-model="localExpirationType"
            class="filter-radio" />
          <label for="obsolete_days" class="radio-label">days after becoming obsolete</label>
        </div>
      </div>
      <div v-if="localExpirationType === 'obsolete_days' && badgeStatus !== 'Given' && badgeStatus !== 'Obsolete'"
        class="d-flex align-center days-input-wrapper">
        <input v-model.number="localDays" type="number" class="days-input"
          :class="{ 'days-input-changed': hasDaysChanged }" />
      </div>
    </div>
    <div v-if="localExpirationType == 'on_this_date'" class="expiration-date-editor-footer">
      <StandardButton v-if="isNew" :label="'Add'" type="magenta" :variant="!canSave ? 'primary' : 'secondary'"
        size="normal" @click="save" :disabled="!canSave" style="width: 100%" />
      <template v-else>
        <StandardButton :label="'Save'" type="magenta" :variant="!canSave ? 'primary' : 'secondary'" size="normal"
          @click="save" :disabled="!canSave" style="width: 100%" />
        <StandardButton :label="'Remove expiration date'" type="red" variant="outline" size="normal"
          @click="removeExpiration" style="width: 100%" />
      </template>
      <StandardButton v-if="badgeStatus === 'Obsolete'" :label="'Set to Good Standing'" type="green" variant="primary"
        size="normal" @click="setToGoodStanding" style="width: 100%" />
    </div>
    <div v-if="localExpirationType == 'obsolete_days'" class="expiration-date-editor-footer">
      <StandardButton v-if="isNew" :label="'Add'" type="magenta" :variant="!canSave ? 'primary' : 'secondary'"
        size="normal" @click="save" :disabled="!canSave" style="width: 100%" />
      <template v-else>
        <div class="obsolete-days-buttons">
          <div>
            <StandardButton :label="'Save'" type="magenta" :variant="!canSave ? 'primary' : 'secondary'" size="normal"
              @click="save" :disabled="!canSave" />
          </div>
          <div class="w-100">
            <StandardButton :label="'Make Obsolete'" type="red" variant="tertiary" size="normal" @click="makeObsolete"
              class="w-100" />
          </div>
        </div>
        <div class="w-100">
          <StandardButton :label="'Remove expiration rule'" type="red" variant="outline" size="normal"
            @click="removeExpiration" class="w-100" />
        </div>
      </template>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from "vue";
import BadgeExpirationWarning from "@components/BadgeExpirationWarning/BadgeExpirationWarning.vue";
import DatePicker from "@components/DatePicker/DatePicker.vue";
import StandardButton from "@components/StandardButton/StandardButton.vue";

const props = defineProps({
  days: {
    type: Number,
    required: false,
    default: 0,
  },
  badgeStatus: {
    type: String,
    required: false,
    default: "Given",
    validator: (value) => ["Given", "Good Standing", "Obsolete"].includes(value),
  },
  icon: {
    type: String,
    required: false,
    default: "add",
    validator: (value) => ["add", "edit", "if_obsolete", "soon", "tonight"].includes(value),
  },
  action: {
    type: String,
    required: false,
    default: "Add",
    validator: (value) => ["Add", "Save", "View"].includes(value),
  },
  policy: {
    type: String,
    required: false,
    default: "Date",
    validator: (value) => ["Date", "Days"].includes(value),
  },
  date: {
    type: Date,
    required: false,
    default: null,
  },
  trianglePosition: {
    type: String,
    required: false,
    default: "top",
    validator: (value) => ["top", "bottom"].includes(value),
  },
  triangleAlign: {
    type: String,
    required: false,
    default: "right",
    validator: (value) => ["left", "right", "center"].includes(value),
  },
  triangleOffset: {
    type: String,
    required: false,
    default: "24px",
  },
});

const emit = defineEmits(["close", "save", "remove", "setToGoodStanding", "makeObsolete"]);

const ensureDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) return value;
  return new Date(value);
};

const getDefaultDate = () => {
  const date = new Date();
  date.setDate(date.getDate() + 125);
  date.setHours(0, 0, 0, 0);
  return date;
};

const minDate = computed(() => {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  tomorrow.setHours(0, 0, 0, 0);
  return tomorrow.toISOString();
});

const isNew = computed(() => props.action === "Add");

const isExpirationProvided = computed(() => {
  if (props.policy === "Days") return props.days && props.days > 0;
  if (props.policy === "Date") return props.date !== null;
  return false;
});
const originalExpirationDate = ref(ensureDate(props.date));
const originalDays = ref(props.days);

const getInitialExpirationType = () => {
  // When badge status is "Given" or "Obsolete", only allow date-based expiration
  if (props.badgeStatus === "Given" || props.badgeStatus === "Obsolete") return "on_this_date";
  if (props.policy === "Days") return "obsolete_days";
  if (props.policy === "Date") return "on_this_date";
  return "on_this_date";
};

const localExpirationType = ref(getInitialExpirationType());
const localExpirationDate = ref(ensureDate(props.date) || getDefaultDate());
const localDays = ref(props.days);

watch(
  () => props.date,
  (newDate) => {
    const date = ensureDate(newDate);
    localExpirationDate.value = date || getDefaultDate();
    originalExpirationDate.value = date;
  }
);

watch(
  () => props.policy,
  (newPolicy) => {
    if (newPolicy === "Days") {
      localExpirationType.value = "obsolete_days";
    } else if (newPolicy === "Date") {
      localExpirationType.value = "on_this_date";
    }
  }
);

watch(
  () => props.days,
  (newDays) => {
    localDays.value = newDays;
    originalDays.value = newDays;
  }
);

watch(
  () => props.badgeStatus,
  (newStatus) => {
    // Force date-based expiration when badge status is "Given" or "Obsolete"
    if (newStatus === "Given" || newStatus === "Obsolete") {
      localExpirationType.value = "on_this_date";
    }
  }
);

const isValidDaysValue = computed(() => {
  return (
    localDays.value !== null &&
    localDays.value !== undefined &&
    localDays.value !== "" &&
    !isNaN(localDays.value) &&
    localDays.value > 0
  );
});

const hasDaysChanged = computed(() => {
  return localDays.value !== originalDays.value;
});

const hasChanges = computed(() => {
  if (localExpirationType.value === "on_this_date") {
    if (!localExpirationDate.value || !originalExpirationDate.value)
      return false;

    // Dates are ensured to be Date objects through ensureDate function
    return (
      localExpirationDate.value.getTime() !==
      originalExpirationDate.value.getTime()
    );
  }

  if (localExpirationType.value === "obsolete_days") {
    if (!isValidDaysValue.value) {
      return false;
    }
    return hasDaysChanged.value;
  }

  return false;
});

const canSave = computed(() => {
  if (props.action === "View") {
    return false;
  }

  if (props.action === "Save") {
    if (localExpirationType.value === "obsolete_days") {
      return isValidDaysValue.value;
    }
    return true;
  }

  if (isNew.value) {
    if (localExpirationType.value === "obsolete_days") {
      return isValidDaysValue.value;
    }
    return true;
  }
  return hasChanges.value;
});

const handleApplyDate = (date) => {
  localExpirationDate.value = date;
};

const save = () => {
  const data = {
    expirationType: localExpirationType.value,
    expirationDate:
      localExpirationType.value === "on_this_date"
        ? localExpirationDate.value
        : null,
    days:
      localExpirationType.value === "obsolete_days"
        ? parseInt(localDays.value)
        : null,
  };
  emit("save", data);
};

const removeExpiration = () => {
  emit("remove");
};

const setToGoodStanding = () => {
  emit("setToGoodStanding");
};

const makeObsolete = () => {
  emit("makeObsolete", {
    days: localDays.value
  });
};

const trianglePositionClass = computed(() => {
  return `triangle-${props.trianglePosition}`;
});

const triangleStyle = computed(() => {
  const styles = {};

  if (props.triangleAlign === "right") {
    styles.right = props.triangleOffset;
    styles.left = "auto";
    if (props.trianglePosition === "top") {
      styles.transform = "rotate(135deg)";
    } else {
      styles.transform = "rotate(-45deg)";
    }
  } else if (props.triangleAlign === "left") {
    styles.left = props.triangleOffset;
    styles.right = "auto";
    if (props.trianglePosition === "top") {
      styles.transform = "rotate(135deg)";
    } else {
      styles.transform = "rotate(-45deg)";
    }
  } else if (props.triangleAlign === "center") {
    styles.left = "50%";
    styles.right = "auto";
    // Transform is handled by CSS classes based on position
    if (props.trianglePosition === "top") {
      styles.transform = "translateX(-50%) rotate(135deg)";
    } else {
      styles.transform = "translateX(-50%) rotate(-45deg)";
    }
  }

  return styles;
});
</script>

<style scoped>
.expiration-date-editor {
  width: 330px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  border-radius: 8px;
  border: 1px solid #D0D5DD;
  background: #7F56D9;
  position: relative;
  gap: 6px;
  font-family: Inter !important;
  z-index: 10000;
}

.expiration-date-editor-header {
  display: flex;
  align-items: center;
  width: 100%;
  padding: 10px 22px;
}

.expiration-date-editor-content {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.close-exp {
  position: absolute;
  right: 11px;
}

.expiration-date-picker,
.expiration-radios {
  padding: 10px 22px;
}

.date-picker-changed ::v-deep(.date-picker-display) {
  border: 1px solid #d6bbfb;
  background: #f2edff;
  box-shadow: 0 1px 2px 0 rgba(16, 24, 40, 0.05);
}

.radio-label {
  font-family: Inter;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0%;
  color: #344054;
}

.radio-wrapper {
  display: flex;
  align-items: center;
}

.filter-radio {
  appearance: none;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: 2px solid #d0d5dd;
  cursor: pointer;
  margin-right: 8px;
  position: relative;
  flex-shrink: 0;
}

.filter-radio:checked {
  border-color: white;
  background-color: white;
}

.filter-radio:checked::after {
  content: "";
  position: absolute;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: #7f56d9;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

.filter-radio:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.filter-radio:disabled+.radio-label {
  opacity: 0.5;
  cursor: not-allowed;
}

.radio-label {
  font-family: Inter;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0%;
  color: white;
  cursor: pointer;
}

.days-input {
  width: 80px;
  height: 40px;
  padding: 8px 12px;
  border-radius: 8px;
  border: 1px solid #d0d5dd;
  background: #fff;
  font-family: Inter;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #475467;
  outline: none;
  transition: all 0.2s;
}

.days-input:focus {
  border-color: #7f56d9;
  box-shadow: 0 0 0 4px rgba(127, 86, 217, 0.1);
}

.days-input:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  background: #f9fafb;
}

.days-input::-webkit-inner-spin-button,
.days-input::-webkit-outer-spin-button {
  -webkit-appearance: none;
  appearance: none;
  margin: 0;
}

.days-input[type="number"] {
  -moz-appearance: textfield;
  appearance: textfield;
}

.days-input-wrapper {
  padding: 10px 22px;
}

.days-input-changed {
  border: 1px solid #d6bbfb !important;
  background: #f2edff !important;
  box-shadow: 0 1px 2px 0 rgba(16, 24, 40, 0.05);
}

.expiration-date-editor-footer {
  display: flex;
  padding: 32px 24px 24px;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  align-self: stretch;
  gap: 12px;
}

.obsolete-days-buttons {
  display: flex;
  gap: 12px;
  width: 100%;
}

.w-100 {
  width: 100%;
}

.triangle {
  height: 20px;
  width: 20px;
  border: 1px solid #D0D5DD;
  background: #7F56D9;
  position: absolute;
  clip-path: polygon(0% 0%, 100% 100%, 0% 100%);
  transition: all 0.2s ease;
  z-index: 1;
}

.triangle-top {
  top: -11.5px;
  bottom: auto;
  transform: rotate(135deg);
}

.triangle-bottom {
  bottom: -10px;
  top: auto;
  transform: rotate(-45deg);
}

::v-deep(.expire-label.soon) {
  max-width: 235px;
}

::v-deep(.future) {
  color: white !important;
}

::v-deep(.standard-button--red.standard-button--outline.standard-button--default) {
  background-color: white !important;
}

::v-deep(.mdi-close::before) {
  color: white !important;
}
</style>
